/**
 * Year6GenerateModal — the "Wow Moment" full-screen takeover.
 *
 *   click → instant takeover → 4 staged progress steps (1.8s minimum)
 *   → reveal (cover preview + confetti + serif headline)
 *   → download primary CTA + share block (WhatsApp / Copy link / Email)
 *
 * Goal: turn a 2-3s technical wait into a 5-second emotional event that
 * triggers immediate sharing — the funnel's growth-loop entry point.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  X, Check, CircleNotch, Download, WhatsappLogo, Copy, EnvelopeSimple,
  Sparkle, GraduationCap, ArrowDown, Eye,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { label: 'Preparing your class memories' },
  { label: 'Designing each page' },
  { label: 'Adding personalised certificates' },
  { label: 'Finalising your Memory Book' },
];

const MIN_TOTAL_MS = 1800;

export default function Year6GenerateModal({
  open, onClose, eventId, slug, schoolName, students = [], firstPhotoUrl,
}) {
  const [phase, setPhase] = useState('progress'); // 'progress' | 'reveal' | 'downloading'
  const [stepIdx, setStepIdx] = useState(0);
  const [error, setError] = useState('');
  const [downloadUrl] = useState(`${API}/events/${eventId}/year6/memory-book/download`);
  const downloadLinkRef = useRef(null);
  const startedAt = useRef(0);
  const stepTimers = useRef([]);
  const fadeTimer = useRef(null);

  // Personal "Adding Oliver, Olivia and 27 others" line
  const personalLine = useMemo(() => {
    if (!students || students.length === 0) return null;
    const names = students.slice(0, 2).map((s) => (s.name || '').split(' ')[0]).filter(Boolean);
    const others = Math.max(0, students.length - names.length);
    if (names.length === 0) return null;
    if (others === 0) return `Adding ${names.join(' and ')}…`;
    return `Adding ${names.join(', ')} and ${others} other${others === 1 ? '' : 's'}…`;
  }, [students]);

  // Run the staged progress loop on open
  useEffect(() => {
    if (!open) return;
    setPhase('progress');
    setStepIdx(0);
    setError('');
    startedAt.current = Date.now();

    // Stage steps every 450ms for first 3, hold last step
    stepTimers.current = [
      setTimeout(() => setStepIdx(1), 450),
      setTimeout(() => setStepIdx(2), 900),
      setTimeout(() => setStepIdx(3), 1350),
    ];

    // Hold the final step until MIN_TOTAL_MS, then transition to reveal.
    // The actual PDF is fetched lazily by the user's click on the Download
    // <a> below — no need to warm the cache here (would log 405 on HEAD).
    let cancelled = false;
    fadeTimer.current = setTimeout(() => {
      if (cancelled) return;
      setStepIdx(4); // all complete
      setTimeout(() => setPhase('reveal'), 300);
    }, MIN_TOTAL_MS);

    return () => {
      cancelled = true;
      stepTimers.current.forEach(clearTimeout);
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
    };
  }, [open, downloadUrl]);

  if (!open) return null;

  const handleDownload = () => {
    setPhase('downloading');
    if (downloadLinkRef.current) downloadLinkRef.current.click();
    setTimeout(() => setPhase('reveal'), 2400); // give browser time to start the file save
  };

  const parentUrl = typeof window !== 'undefined' ? `${window.location.origin}/year6/${slug}/view` : '';
  const shareMessage =
    `🎓 Our Year 6 Memory Book is ready!\n\nWe've created something really special with photos, messages and a personalised certificate for every child.\n\nTake a look 👇\n${parentUrl}`;

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`, '_blank');
  };
  const handleCopyLink = async () => {
    try { await navigator.clipboard.writeText(parentUrl); toast.success('Parent link copied'); }
    catch { toast.error('Could not copy'); }
  };
  const handleEmail = () => {
    const subject = `Our Year 6 Memory Book is ready 🎓`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(shareMessage)}`;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[140] overflow-hidden"
        data-testid="year6-generate-modal"
      >
        {/* Animated cyan→purple aurora background */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 30% 20%, rgba(56,189,248,0.22), transparent 55%), radial-gradient(ellipse at 70% 80%, rgba(168,85,247,0.28), transparent 55%), linear-gradient(180deg, #050617 0%, #0F1014 60%, #04050f 100%)',
          }}
        />
        {/* Floating particles */}
        <FloatingParticles />

        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-5 right-5 z-10 w-10 h-10 rounded-full bg-white/8 hover:bg-white/15 backdrop-blur text-white/70 hover:text-white flex items-center justify-center"
          data-testid="year6-generate-close"
        >
          <X size={18} weight="bold" />
        </button>

        {/* Content */}
        <div className="relative h-full w-full flex items-center justify-center p-6 overflow-y-auto">
          <AnimatePresence mode="wait">
            {phase === 'progress' && (
              <motion.div
                key="progress"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
                className="w-full max-w-md text-center"
                data-testid="year6-generate-progress"
              >
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-300/12 border border-amber-300/30 text-amber-200 text-[10px] font-black uppercase tracking-[0.2em] mb-5">
                  <Sparkle size={11} weight="fill" /> Creating
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl font-black leading-tight mb-2 text-white">
                  🎓 Creating your Year 6 Memory Book
                </h2>
                <p className="text-white/60 text-sm mb-8">
                  This brings together every photo, message and moment from your class.
                </p>

                <ul className="space-y-2.5 text-left mb-6" data-testid="year6-generate-steps">
                  {STEPS.map((step, idx) => {
                    const done = idx < stepIdx;
                    const active = idx === stepIdx;
                    return (
                      <li
                        key={step.label}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${active ? 'bg-white/8 border border-amber-300/25' : 'bg-white/3 border border-white/5'}`}
                        data-testid={`year6-generate-step-${idx}`}
                      >
                        <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${done ? 'bg-emerald-400/15 text-emerald-300' : active ? 'bg-amber-300/15 text-amber-200' : 'bg-white/[0.05] text-white/30'}`}>
                          {done ? <Check size={14} weight="bold" /> : active ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <span className="text-[10px] font-black">{idx + 1}</span>}
                        </span>
                        <span className={`text-sm font-semibold ${done ? 'text-white/55 line-through decoration-white/15' : active ? 'text-white' : 'text-white/45'}`}>
                          {step.label}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {personalLine && (
                  <p className="text-amber-200/80 text-[12px] italic" data-testid="year6-generate-personal-line">
                    {personalLine}
                  </p>
                )}

                {error && <p className="text-rose-300 text-sm mt-4">{error}</p>}
              </motion.div>
            )}

            {(phase === 'reveal' || phase === 'downloading') && (
              <motion.div
                key="reveal"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="w-full max-w-lg text-center"
                data-testid="year6-generate-reveal"
              >
                <ConfettiBurst />
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-400/15 border border-emerald-400/35 text-emerald-300 text-[10px] font-black uppercase tracking-[0.2em] mb-4">
                  <Check size={11} weight="bold" /> Ready
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl font-black leading-tight mb-2 text-white">
                  🎉 Your Memory Book is ready!
                </h2>
                <p className="text-white/60 text-sm mb-6 max-w-sm mx-auto">
                  A beautiful keepsake for your class — ready to download and share.
                </p>

                <CoverPreview schoolName={schoolName} firstPhotoUrl={firstPhotoUrl} />

                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 mt-6 max-w-sm mx-auto">
                  <a
                    ref={downloadLinkRef}
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleDownload}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-[14px] font-black text-white shadow-2xl"
                    style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)', boxShadow: '0 10px 40px -10px rgba(139,92,246,0.6), 0 0 0 1px rgba(255,255,255,0.06) inset' }}
                    data-testid="year6-generate-download"
                  >
                    {phase === 'downloading' ? (
                      <><CircleNotch size={14} weight="bold" className="animate-spin" /> Download starting…</>
                    ) : (
                      <><Download size={15} weight="bold" /> Download Memory Book</>
                    )}
                  </a>
                  <a
                    href={`/year6/${slug}/view`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-[13px] font-bold text-white border border-white/15 bg-white/[0.04] hover:bg-white/[0.08]"
                    data-testid="year6-generate-preview-again"
                  >
                    <Eye size={13} weight="fill" /> Preview again
                  </a>
                </div>

                {phase === 'downloading' && (
                  <p className="text-white/50 text-[11px] mt-3" data-testid="year6-generate-download-feedback">
                    Your file should open shortly — <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-white">click here if it doesn't</a>
                  </p>
                )}

                {/* Share block — the growth-loop trigger */}
                <div className="mt-7 pt-6 border-t border-white/10 max-w-sm mx-auto" data-testid="year6-generate-share">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-white/55 mb-3 flex items-center justify-center gap-1.5">
                    <ArrowDown size={11} weight="bold" /> Share with parents
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={handleWhatsApp} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-emerald-500/85 hover:bg-emerald-500 text-white text-[12px] font-bold transition-colors" data-testid="year6-generate-share-whatsapp">
                      <WhatsappLogo size={14} weight="fill" /> WhatsApp
                    </button>
                    <button onClick={handleCopyLink} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-white/8 hover:bg-white/15 text-white text-[12px] font-bold transition-colors" data-testid="year6-generate-share-copy">
                      <Copy size={13} weight="bold" /> Copy link
                    </button>
                    <button onClick={handleEmail} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-white/8 hover:bg-white/15 text-white text-[12px] font-bold transition-colors" data-testid="year6-generate-share-email">
                      <EnvelopeSimple size={13} weight="bold" /> Email
                    </button>
                  </div>
                  <p className="text-white/40 text-[11px] italic mt-3">
                    Parents love this — it's something they'll keep forever 💛
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ---------- Cover preview thumbnail ---------- */
function CoverPreview({ schoolName, firstPhotoUrl }) {
  return (
    <motion.div
      initial={{ y: 8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.18, duration: 0.5, ease: 'easeOut' }}
      className="relative mx-auto rounded-2xl overflow-hidden"
      style={{
        width: 220, aspectRatio: '3 / 4',
        boxShadow: '0 30px 70px -20px rgba(0,0,0,0.8), 0 8px 30px -6px rgba(139,92,246,0.35)',
      }}
      data-testid="year6-generate-cover-preview"
    >
      {firstPhotoUrl ? (
        <img src={firstPhotoUrl.startsWith('http') ? firstPhotoUrl : `${BACKEND_URL}${firstPhotoUrl}`} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 60%, #F59E0B 100%)' }} />
      )}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.92) 100%)' }} />
      <div className="absolute inset-0 flex flex-col justify-end p-4 text-white">
        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/18 border border-white/30 text-[9px] font-black uppercase tracking-[0.16em] self-start mb-2">
          <GraduationCap size={9} weight="fill" /> Year 6 Leavers
        </div>
        <p className="font-serif text-lg font-black leading-tight mb-0.5" style={{ fontFamily: 'Georgia, serif' }}>
          {schoolName || 'Year 6'}
        </p>
        <p className="text-white/85 text-[10px] font-semibold">Memory Book · Class of 2026</p>
      </div>
    </motion.div>
  );
}

/* ---------- Confetti burst (CSS-only, ~30 particles) ---------- */
function ConfettiBurst() {
  const [particles] = useState(() => {
    const colours = ['#FACC15', '#F472B6', '#A78BFA', '#34D399', '#60A5FA', '#FB923C'];
    return Array.from({ length: 32 }).map((_, i) => ({
      id: i,
      x: (Math.random() - 0.5) * 360,
      y: -(180 + Math.random() * 220),
      r: Math.random() * 720 - 360,
      d: 700 + Math.random() * 400,
      c: colours[i % colours.length],
      s: 6 + Math.random() * 6,
    }));
  });
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
      {particles.map((p) => (
        <motion.span
          key={p.id}
          initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
          animate={{ x: p.x, y: p.y, opacity: 0, rotate: p.r }}
          transition={{ duration: p.d / 1000, ease: 'easeOut' }}
          className="absolute top-1/2 left-1/2 rounded-sm"
          style={{ width: p.s, height: p.s * 0.6, background: p.c }}
        />
      ))}
    </div>
  );
}

/* ---------- Floating particles for progress phase ---------- */
function FloatingParticles() {
  const dots = useMemo(() => Array.from({ length: 14 }).map((_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 5,
    duration: 8 + Math.random() * 6,
    size: 2 + Math.random() * 3,
  })), []);
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      {dots.map((d) => (
        <span
          key={d.id}
          className="absolute rounded-full bg-white/40"
          style={{
            left: `${d.left}%`, bottom: '-10px',
            width: d.size, height: d.size,
            animation: `y6floatUp ${d.duration}s ${d.delay}s linear infinite`,
            opacity: 0.3,
          }}
        />
      ))}
      <style>{`@keyframes y6floatUp { from { transform: translateY(0); opacity: 0; } 10% { opacity: 0.4; } 90% { opacity: 0.4; } to { transform: translateY(-110vh); opacity: 0; } }`}</style>
    </div>
  );
}
